import { type Options as RRuleOptions, RRule, type Weekday } from 'rrule'

import { type IsoWeekday, normalizeRule, type RecurrenceFrequency, type RecurrenceRule } from './types'

const ISO_TO_RRULE_WEEKDAY: Record<IsoWeekday, Weekday> = {
  1: RRule.MO,
  2: RRule.TU,
  3: RRule.WE,
  4: RRule.TH,
  5: RRule.FR,
  6: RRule.SA,
  7: RRule.SU,
}

const FREQ_MAP: Record<RecurrenceFrequency, number> = {
  daily: RRule.DAILY,
  weekly: RRule.WEEKLY,
  monthly: RRule.MONTHLY,
  yearly: RRule.YEARLY,
}

export function toRRuleOptions(rule: RecurrenceRule, dtstart: Date, tz?: string): Partial<RRuleOptions> {
  const r = normalizeRule(rule)
  const opts: Partial<RRuleOptions> = {
    freq: FREQ_MAP[r.frequency],
    interval: Math.max(1, r.interval),
    dtstart,
  }
  if (tz) {
    // rrule advances by "calendar units" in `tzid` — keeps wall-clock
    // stable across DST shifts. Without this, weekly across a DST
    // boundary drifts by an hour.
    opts.tzid = tz
  }

  if (r.frequency === 'weekly' && r.weekDays?.length) {
    opts.byweekday = r.weekDays.map((d) => ISO_TO_RRULE_WEEKDAY[d])
  }

  if (r.frequency === 'monthly') {
    if (r.monthlyMode === 'nth-weekday' && r.nthWeekday) {
      opts.byweekday = [ISO_TO_RRULE_WEEKDAY[r.nthWeekday.weekday]]
      opts.bysetpos = [r.nthWeekday.position]
    } else if (typeof r.monthDay === 'number') {
      opts.bymonthday = [r.monthDay]
    }
  }

  return opts
}

export function toRRuleString(rule: RecurrenceRule, dtstart: Date, tz?: string): string {
  return new RRule(toRRuleOptions(rule, dtstart, tz)).toString()
}
